import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useToast, ToastContainer } from "../../components/common/Toast";
import DeleteConfirmModal from "../../components/decks/DeleteConfirmModal";
import NewsletterForm from "../../components/admin/NewsletterForm";
import {
  newsletterAdminService,
  Newsletter,
  NewsletterStatus,
  CreateNewsletterInput,
} from "../../services/newsletterAdminService";

const statusBadge = (status: NewsletterStatus) => {
  const styles: Record<NewsletterStatus, string> = {
    active: "bg-green-600/20 text-green-400 border-green-600/50",
    paused: "bg-gray-600/20 text-gray-400 border-gray-600/50",
  };
  return (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status]}`}
    >
      {status.toUpperCase()}
    </span>
  );
};

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
};

const AdminNewslettersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Newsletter | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await newsletterAdminService.list();
      setNewsletters(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load newsletters");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (input: CreateNewsletterInput) => {
    try {
      setSubmittingForm(true);
      const created = await newsletterAdminService.create(input);
      setNewsletters((prev) => [created, ...prev]);
      showSuccess("Newsletter created");
      setShowForm(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to create newsletter");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleToggleStatus = async (nl: Newsletter) => {
    const next: NewsletterStatus = nl.status === "active" ? "paused" : "active";
    try {
      setActionId(nl.id);
      const updated = await newsletterAdminService.setStatus(nl.id, next);
      setNewsletters((prev) => prev.map((n) => (n.id === nl.id ? updated : n)));
      showSuccess(next === "active" ? "Newsletter resumed" : "Newsletter paused");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await newsletterAdminService.remove(deleteTarget.id);
      setNewsletters((prev) => prev.filter((n) => n.id !== deleteTarget.id));
      showSuccess("Newsletter deleted");
      setDeleteTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to delete newsletter");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Newsletters"
        subtitle="Create, schedule, and broadcast newsletters to all users"
        action={
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
          >
            + Create Newsletter
          </button>
        }
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Loading newsletters...</p>
        </div>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-6 mb-8">
          <p className="text-red-300">{error}</p>
        </div>
      ) : newsletters.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-12 text-center">
          <p className="text-gray-400 text-lg mb-2">No newsletters yet</p>
          <p className="text-gray-500 text-sm mb-6">
            Create your first newsletter to schedule automated broadcasts.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all"
          >
            + Create Newsletter
          </button>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Cadence
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Next run
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Last sent
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {newsletters.map((n) => (
                <tr
                  key={n.id}
                  className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="text-white text-sm font-medium">{n.name}</div>
                    {n.description && (
                      <div className="text-gray-500 text-xs mt-1">{n.description}</div>
                    )}
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-sm capitalize">
                    {n.cadence}
                  </td>
                  <td className="py-4 px-4">{statusBadge(n.status)}</td>
                  <td className="py-4 px-4 text-gray-300 text-sm">
                    {formatDate(n.next_run_at)}
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-sm">
                    {formatDate(n.last_sent_at)}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/admin/newsletters/${n.id}`)}
                        className="px-3 py-1.5 bg-purple-600/20 text-purple-300 border border-purple-600/50 rounded-md text-xs font-semibold hover:bg-purple-600/30 hover:border-purple-500 transition-all"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleToggleStatus(n)}
                        disabled={actionId === n.id}
                        className="px-3 py-1.5 bg-gray-600/20 text-gray-300 border border-gray-600/50 rounded-md text-xs font-semibold hover:bg-gray-600/30 hover:border-gray-500 transition-all disabled:opacity-50"
                      >
                        {n.status === "active" ? "Pause" : "Resume"}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(n)}
                        className="px-3 py-1.5 bg-red-600/20 text-red-400 border border-red-600/50 rounded-md text-xs font-semibold hover:bg-red-600/30 hover:border-red-500 transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NewsletterForm
          submitting={submittingForm}
          onCancel={() => setShowForm(false)}
          onSubmit={handleCreate}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Delete newsletter?"
          message={`This permanently deletes "${deleteTarget.name}" and all of its editions. This cannot be undone.`}
          confirmLabel="Delete"
          isDeleting={deleting}
          onCancel={() => (deleting ? undefined : setDeleteTarget(null))}
          onConfirm={handleDelete}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default AdminNewslettersPage;
